import { useState } from 'react';
import { MasterFlightRecord, validateField } from '../../../../../../utils/types';

type FieldErrors = {
  [K in keyof MasterFlightRecord]?: string | null;
};

/**
 * Custom hook for managing form validation state
 * Tracks touched fields and error messages
 */
export function useFormValidation() {
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [touchedFields, setTouchedFields] = useState<Set<keyof MasterFlightRecord>>(new Set());

  // Validate a field and update error state
  const validateAndSetError = (fieldName: keyof MasterFlightRecord, value: MasterFlightRecord[keyof MasterFlightRecord], record: MasterFlightRecord) => {
    const error = validateField(fieldName, value, record);
    setFieldErrors(prev => ({ ...prev, [fieldName]: error }));
    setTouchedFields(prev => new Set(prev).add(fieldName));
  };

  // Handle field blur - validate the field
  const handleBlur = (fieldName: keyof MasterFlightRecord, value: MasterFlightRecord[keyof MasterFlightRecord], record: MasterFlightRecord) => {
    validateAndSetError(fieldName, value, record);
  };

  // Get error message for a field (only show if touched)
  const getFieldError = (fieldName: keyof MasterFlightRecord): string | null => {
    if (!touchedFields.has(fieldName)) return null;
    return fieldErrors[fieldName] || null;
  };

  // Clear error for a specific field
  const clearFieldError = (fieldName: keyof MasterFlightRecord) => {
    setFieldErrors(prev => ({ ...prev, [fieldName]: null }));
  };

  // Real-time validation (called on change if field was touched)
  const revalidateIfTouched = (fieldName: keyof MasterFlightRecord, value: MasterFlightRecord[keyof MasterFlightRecord], record: MasterFlightRecord) => {
    if (touchedFields.has(fieldName)) {
      const error = validateField(fieldName, value, record);
      setFieldErrors(prev => ({ ...prev, [fieldName]: error }));
    }
  };

  // Validate all fields and mark them as touched (used when Save is clicked)
  // Returns true if there are any errors, false if validation passed
  const validateAllFields = (record: MasterFlightRecord): boolean => {
    const fieldsToValidate: (keyof MasterFlightRecord)[] = [
      'flightOwner',
      'flightNo',
      'iataServiceTypeCode',
      'itineraryVarId',
      'startDate',
      'endDate',
      'frequency',
      'seasonId',
      'operationalSuffix',
    ];

    const newErrors: FieldErrors = {};
    const newTouchedFields = new Set<keyof MasterFlightRecord>();
    let hasErrors = false;

    fieldsToValidate.forEach(fieldName => {
      const error = validateField(fieldName, record[fieldName], record);
      if (error) {
        newErrors[fieldName] = error;
        hasErrors = true;
      }
      newTouchedFields.add(fieldName);
    });

    setFieldErrors(prev => ({ ...prev, ...newErrors }));
    setTouchedFields(prev => {
      const updated = new Set(prev);
      newTouchedFields.forEach(field => updated.add(field));
      return updated;
    });

    return hasErrors;
  };

  // Reset validation state (used when modal closes/opens)
  const resetValidation = () => {
    setFieldErrors({});
    setTouchedFields(new Set());
  };

  return {
    fieldErrors,
    touchedFields,
    validateAndSetError,
    handleBlur,
    getFieldError,
    clearFieldError,
    revalidateIfTouched,
    validateAllFields,
    resetValidation,
  };
}
